import { map as rxjsmap, Observable } from 'rxjs';
import { ActivatedRoute } from '@angular/router';

import { Injectable } from '@angular/core';
import { environment } from 'src/environments/environment';

import { BaseServices } from './../../kuba.services';
import { HttpClient, HttpContext, HttpParams } from '@angular/common/http';
import { SKIP_ERROR_TOAST } from 'src/app/shared/http-context-tokens';
import {
  DocumentedTrainingPage,
  EmployeeApprovalDocument,
  PrivateDocumentUploadListResponse
} from '../models/employeeDocument';
import { updateApprovedStatus } from '../helpers/employee-document.helper';

@Injectable()
export class EmployeePrivateDocumentServices {
  item: Object;
  constructor(private http: HttpClient) {}

  list(empId: number, type: any) {
    return this.http
      .get(
        environment.BASE_URL +
          '/employee/' +
          empId +
          '/private-docs' +
          '/' +
          parseInt(type)
      )
      .pipe(
        rxjsmap((result: any) => {
          let data = result || [];
          if (data.length > 0) {
            data.forEach((d: any) => {
              d.CreatedOn = new Date(d.CreatedOn);
              d.ModifiedOn = new Date(d.ModifiedOn);
            });
            result.data = data;
            return result;
          }
        })
      );
  }

  /**
   * Gets one page of an employee's documented-training documents, ordered by file name.
   * @param employeeId The employee whose documents to list
   * @param pageNumber 1-based page number
   * @param pageSize Documents per page, at most 100
   * @param search Optional text the file name must contain
   * @param awaitingMyConfirmation Only the documents the logged-in user is the registered
   * trainer of and has not confirmed yet; these come without article or checklist content
   * @returns The page of documents and the total number of matching documents
   */
  getDocumentedTrainingPage(
    employeeId: number,
    pageNumber: number,
    pageSize: number,
    search = '',
    awaitingMyConfirmation = false
  ): Observable<DocumentedTrainingPage> {
    let params = new HttpParams()
      .set('pageNumber', pageNumber)
      .set('pageSize', pageSize)
      .set('awaitingMyConfirmation', awaitingMyConfirmation);
    if (search) {
      params = params.set('search', search);
    }
    return this.http.get<DocumentedTrainingPage>(
      environment.BASE_URL_V2 +
        '/kuba/v1/employees/' +
        employeeId +
        '/documented-training',
      { params }
    );
  }

  delete(id: number) {
    return this.http
      .delete(environment.BASE_URL + '/employee/' + 0 + '/private-doc/' + id)
      .pipe(rxjsmap(result => result));
  }

  save(document: any, empId: number) {
    return this.http
      .post(environment.BASE_URL + '/employeedocument', document)
      .pipe(rxjsmap(result => result));
  }

  /**
   * Uploads multiple documents for one or more employees
   * @param documents List of documents to upload
   * @param empId Pointless param that the backend requires
   * @returns List of uploaded documents
   */
  saveDocs(documents: any): Observable<PrivateDocumentUploadListResponse[]> {
    return this.http.post<PrivateDocumentUploadListResponse[]>(
      environment.BASE_URL + '/employee/' + 0 + '/private-docs',
      documents
    );
  }

  getbyId(id: number) {
    return this.http
      .get(environment.BASE_URL + '/employee/' + id + '/private-doc/' + id)
      .pipe(rxjsmap(result => result));
  }

  update(
    document: any,
    isSendApproval: any,
    empId: any,
    FeatureKey: number,
    type: any
  ) {
    return this.http
      .put(
        environment.BASE_URL +
          '/employee/' +
          empId +
          '/private-doc/' +
          empId +
          '/' +
          isSendApproval +
          '/' +
          FeatureKey +
          '/' +
          BaseServices.userLanguageId +
          '/' +
          type,
        document
      )
      .pipe(rxjsmap(result => result));
  }

  /**
   * Updates approval documents
   * @param documents List of documents to update
   * @param isSendApproval Flag indicating if approval is sent
   * @returns Observable of the update result
   */
  updateApprovalDocuments(documents: any, isSendApproval: number) {
    return this.http
      .put(
        environment.BASE_URL +
          '/employee/' +
          0 +
          '/approval-docs/' +
          isSendApproval +
          '/' +
          BaseServices.userLanguageId,
        documents
      )
      .pipe(rxjsmap(result => result));
  }

  /**
   * Sends an approval request notification to employees with unapproved documents.
   * Uses SKIP_ERROR_TOAST so the global interceptor does not show a generic error toast —
   * the caller is responsible for handling errors (e.g. 404 = no documents to approve).
   * @param employeeIds List of employee IDs to notify
   * @param isDocumentedTraining True if sending for documented training, false for private documents
   * @returns Observable of the result
   */
  sendApprovalRequest(
    employeeIds: { employeeId: number }[],
    isDocumentedTraining = false
  ) {
    return this.http.put(
      environment.BASE_URL_V2 + '/kuba/v1/employees/send-approval-request',
      employeeIds,
      {
        context: new HttpContext().set(SKIP_ERROR_TOAST, true),
        params: { isDocumentedTraining }
      }
    );
  }

  /**
   * Gets the employees eligible to be selected as the "trained by" person for documented training
   * (editors and users with the "Administrate documented training" right).
   * @param businessId The business id
   * @returns Observable of the eligible trainers ({ Id, Name })
   */
  getDocumentedTrainingTrainers(businessId: number) {
    return this.http.get(
      environment.BASE_URL_V2 +
        '/kuba/v1/employees/documented-training-trainers/' +
        businessId
    );
  }

  /**
   * Confirms documented training was given, on behalf of the registered "trained by" person.
   * Uses SKIP_ERROR_TOAST so the caller handles errors directly.
   * @param documentIds List of documented-training document IDs to confirm
   * @param trainedOn The date the training was given (ISO string, today or a past date)
   * @returns Observable of the result
   */
  confirmTraining(documentIds: number[], trainedOn: string) {
    return this.http.put(
      environment.BASE_URL_V2 + '/kuba/v1/employees/confirm-training',
      { documentIds, trainedOn },
      { context: new HttpContext().set(SKIP_ERROR_TOAST, true) }
    );
  }

  /**
   * Registers the "trained by" person on documents that were uploaded without one (#6026).
   * Uses SKIP_ERROR_TOAST so the caller handles errors directly.
   * @param assignments The document id and chosen trainer for each document
   * @param notifyTrainers False when an approval notice is sent straight after, which emails the
   * same trainers itself - saving on its own does notify them
   * @returns Observable of the result
   */
  assignTrainer(
    assignments: { documentId: number; trainedById: number }[],
    notifyTrainers = true
  ) {
    return this.http.put(
      environment.BASE_URL_V2 + '/kuba/v1/employees/assign-trainer',
      { assignments },
      {
        context: new HttpContext().set(SKIP_ERROR_TOAST, true),
        params: { notifyTrainers }
      }
    );
  }

  /**
   * Approves a list of private documents for the logged-in employee.
   * Uses SKIP_ERROR_TOAST so the caller handles errors directly.
   * @param documentIds List of document IDs to approve
   * @returns Observable of the result
   */
  approveDocuments(documentIds: { documentId: number }[]) {
    return this.http.put(
      environment.BASE_URL_V2 + '/kuba/v1/employees/approve',
      documentIds,
      { context: new HttpContext().set(SKIP_ERROR_TOAST, true) }
    );
  }

  updateDoc(docId: any, empId: any, type: any) {
    return this.http
      .put(
        environment.BASE_URL +
          '/employee/' +
          empId +
          '/private-doc/update/' +
          docId,
        {}
      )
      .pipe(rxjsmap(result => result));
  }

  getApprovalDocuments(
    empId,
    bId,
    PortalId,
    type
  ): Observable<EmployeeApprovalDocument[]> {
    return this.http
      .get<EmployeeApprovalDocument[]>(
        environment.BASE_URL +
          '/employee/' +
          empId +
          '/approval-doc/' +
          parseInt(bId) +
          '/' +
          parseInt(PortalId) +
          '/' +
          parseInt(type)
      )
      .pipe(
        rxjsmap(employees => {
          employees.forEach(updateApprovedStatus);
          return employees;
        })
      );
  }

  exportDocumentedTraining(
    businessId: number,
    portalId: number,
    langId: number,
    format: string = 'xlsx',
    employeeIds?: number[],
    documentIds?: number[]
  ) {
    let url: string;
    if (businessId > 0) {
      url =
        environment.BASE_URL +
        '/employee/0/export-documented-training/' +
        businessId +
        '/' +
        langId +
        '?format=' +
        format;
    } else {
      url =
        environment.BASE_URL +
        '/employee/0/export-documented-training/portal/' +
        (portalId || 0) +
        '/' +
        langId +
        '?format=' +
        format;
    }

    if (employeeIds && employeeIds.length > 0) {
      url += '&employeeIds=' + employeeIds.join(',');
    }

    if (documentIds && documentIds.length > 0) {
      url += '&documentIds=' + documentIds.join(',');
    }

    return this.http.get(url, { responseType: 'blob' });
  }
}
